import { XIcon } from '@phosphor-icons/react';
import { useState, type ReactNode } from 'react';
import { Document, Page } from 'react-pdf';
import { Button, Modal } from '@skupreme/shared/ui';

type PreviewProps = {
  open: boolean;
  closeModal: () => void;
  url?: string;
};

/** A wide modal around a file, closed with Esc, the backdrop or the X. */
function Preview({
  open,
  closeModal,
  label,
  children,
}: Omit<PreviewProps, 'url'> & { label: string; children: ReactNode }) {
  return (
    <Modal
      isOpen={open}
      onOpenChange={isOpen => !isOpen && closeModal()}
      aria-label={label}
      className="sk:max-w-[min(1100px,100%)]"
      bodyClassName="sk:flex sk:flex-col sk:items-center"
    >
      <Button
        variant="ghost"
        shape="circle"
        aria-label="Close"
        onPress={closeModal}
        className="sk:self-end"
      >
        <XIcon size={18} />
      </Button>
      <div className="sk:flex sk:max-h-[80vh] sk:w-full sk:flex-col sk:items-center sk:overflow-auto sk:[&_.react-pdf\_\_Page]:mb-8">
        {children}
      </div>
    </Modal>
  );
}

export function PreviewImage({ open, closeModal, url }: PreviewProps) {
  return (
    <Preview open={open} closeModal={closeModal} label="Image preview">
      <img src={url} alt="Preview" className="sk:max-w-full" />
    </Preview>
  );
}

export function PreviewPDF({ open, closeModal, url }: PreviewProps) {
  /* Kept with the url it was counted for, so another file starts from 0. */
  const [loaded, setLoaded] = useState({ url, numPages: 0 });
  const numPages = loaded.url === url ? loaded.numPages : 0;

  return (
    <Preview open={open} closeModal={closeModal} label="PDF preview">
      <Document
        file={url}
        onLoadSuccess={({ numPages: next }) =>
          setLoaded({ url, numPages: next })
        }
      >
        {Array.from({ length: numPages }, (_, index) => (
          <Page key={`page_${index + 1}`} pageNumber={index + 1} />
        ))}
      </Document>
    </Preview>
  );
}
